// apps/web/src/features/crm/my-day/components/MyDayTaskRow.tsx
//
// Single compact row inside a bucket. Renders title + lead public code
// badge + due chip + complete affordance. The complete button is gated
// on `tasks.manage`; viewers see no action area at all.
//
// Lead deep-link: the left column is a nav button that navigates to
// `/leads/:id?tab=tasks` (the episode page tasks tab, #1808).
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { MyDayBucketKind, MyDayItem } from '@magimp/shared';
import { Button } from '../../../../ui/index.js';
import { errorMessage } from '../../../../api/errors.js';
import { useCompleteLeadTask } from '@magimp/shared-hooks';

export interface MyDayTaskRowProps {
  item: MyDayItem;
  bucket: MyDayBucketKind;
  canManage: boolean;
  /** IANA tz from the My Day response — anchors `dueAt` to the same
   *  clock the backend used for bucket math (#1082). */
  timezone: string;
}

const CHIP_TONE: Record<MyDayBucketKind, { bg: string; fg: string }> = {
  overdue: { bg: 'var(--status-error-bg)', fg: 'var(--status-error-fg)' },
  today: { bg: 'var(--status-info-bg)', fg: 'var(--status-info-fg)' },
  upcoming: { bg: 'var(--status-neutral-bg)', fg: 'var(--status-neutral-fg)' },
  blocked: { bg: 'var(--status-neutral-bg)', fg: 'var(--status-neutral-fg)' },
};

export function MyDayTaskRow({ item, bucket, canManage, timezone }: MyDayTaskRowProps) {
  const { t, i18n } = useTranslation('myDay');
  const navigate = useNavigate();
  const mut = useCompleteLeadTask(item.id);
  const dueLabel = formatDueAt(item.dueAt, i18n.language, timezone);
  const tone = CHIP_TONE[bucket];

  return (
    <div
      data-testid={`my-day-row-${item.id}`}
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        padding: '8px 10px',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-1)',
        background: 'var(--bg-0)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button
          type="button"
          data-testid={`my-day-row-${item.id}-open`}
          aria-label={t('row.openLeadAria', { code: item.leadPublicCode })}
          onClick={() => navigate(`/leads/${item.leadId}?tab=tasks`)}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            margin: 0,
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <span style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span
              data-testid={`my-day-row-${item.id}-lead`}
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--fg-2)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {item.leadPublicCode}
            </span>
            <span style={{ fontSize: 14, color: 'var(--fg-1)', overflowWrap: 'anywhere' }}>
              {item.title}
            </span>
          </span>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span
              data-testid={`my-day-row-${item.id}-due`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 8px',
                borderRadius: 'var(--r-full)',
                fontSize: 11,
                fontWeight: 600,
                background: tone.bg,
                color: tone.fg,
              }}
            >
              {dueLabel}
            </span>
            {item.kind === 'STAGE_PRIMARY' && (
              <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('row.stagePrimary')}</span>
            )}
            {item.kind === 'CALLBACK_URGENT' && (
              // #1363: urgent callbacks reuse the error-tone chip primitive so
              //   the row reads as visually urgent, matching the overdue chip.
              <span
                data-testid={`my-day-row-${item.id}-callback-urgent`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 8px',
                  borderRadius: 'var(--r-full)',
                  fontSize: 11,
                  fontWeight: 600,
                  background: 'var(--status-error-bg)',
                  color: 'var(--status-error-fg)',
                }}
              >
                {t('row.callbackUrgent')}
              </span>
            )}
          </span>
        </button>
        {mut.error != null && (
          <div
            data-testid={`my-day-row-${item.id}-error`}
            style={{ fontSize: 11, color: 'var(--status-error-fg)' }}
          >
            {errorMessage(mut.error)}
          </div>
        )}
      </div>
      {canManage && (
        <Button
          size="sm"
          kind="secondary"
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
          data-testid={`my-day-row-${item.id}-complete`}
        >
          {mut.isPending ? t('row.completing') : t('row.complete')}
        </Button>
      )}
    </div>
  );
}

function formatDueAt(iso: string, locale: string, timezone: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    }).format(d);
  } catch {
    // WHY: invalid IANA tz (server drift) — fall back to locale-only
    // rendering rather than crashing the row. The backend re-validates
    // tz with Intl.DateTimeFormat itself, so this branch should never
    // fire in production.
    try {
      return new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d);
    } catch {
      return d.toISOString();
    }
  }
}
